package org.example.project.enginefinanzen

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import kotlinx.browser.document
import org.example.project.engineletter.RenderInvoiceLetterSys
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.engineservice.ServiceApi
import org.example.project.enginetabular.TabularScreen
import org.example.project.manager.LumaticaApiManager
import org.example.project.screens_cus.CUSCeoDashboardStyles
import org.example.project.screens_sys.EingangsbelegeApi
import org.example.project.screens_sys.KundenverbrauchApi
import org.example.project.screens_sys.SysDashboardEinnahmenTable
import org.example.project.screens_sys.UnternehmenstammApi
import org.example.project.service.LoginService
import org.example.project.settings.LogoSettings
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private val financeMonthNames = listOf("Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember")

private fun financeMonthLabel(key: String): String {
    val parts = key.split("-")
    val month = parts.getOrNull(1)?.toIntOrNull()?.takeIf { it in 1..12 } ?: return key
    return "${financeMonthNames[month - 1]} ${parts.firstOrNull().orEmpty()}"
}

private fun financeEuro(value: String): String {
    val number = value.toDoubleOrNull() ?: 0.0
    val formatted = js("new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format")(number) as String
    return "$formatted €"
}

private fun financeAmount(value: String): Double =
    if (',' in value) value.replace(".", "").replace(',', '.').toDoubleOrNull() ?: 0.0
    else value.toDoubleOrNull() ?: 0.0

private object FinanceDashboardApi {
    private val json = Json { ignoreUnknownKeys = true }
    suspend fun load(): List<All> = runCatching {
        json.decodeFromString(ListSerializer(All.AllNames.serializer()), LumaticaApiManager.call("finanzdashboard/dashboard"))
    }.getOrDefault(emptyList())
}

@Composable
fun SysFinanzDashboardScreen() {
    Style(CUSCeoDashboardStyles)
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var financeRows by remember { mutableStateOf(emptyList<All>()) }
    var incomeRows by remember { mutableStateOf(emptyList<All>()) }
    var expenseRows by remember { mutableStateOf(emptyList<All>()) }
    var allExpenseRows by remember { mutableStateOf(emptyList<All>()) }
    var periodIncomeRows by remember { mutableStateOf(emptyList<All>()) }
    var invoiceCustomers by remember { mutableStateOf(emptyList<All>()) }
    var invoiceCompany by remember { mutableStateOf(All()) }
    var invoiceUser by remember { mutableStateOf(All()) }
    var invoiceLogo by remember { mutableStateOf("") }
    var invoiceToOpen by remember { mutableStateOf<All?>(null) }
    var selectedArea by remember { mutableStateOf("EINNAHMEN") }
    var selectedPeriod by remember { mutableStateOf("MONAT") }
    val now = remember { js("new Date()") }
    val year = remember { (now.getFullYear() as Int).toString() }
    val monthNumber = remember { ((now.getMonth() as Int) + 1).toString().padStart(2, '0') }
    val isoMonth = "$year-$monthNumber"
    val billingMonth = "${year}_$monthNumber"

    suspend fun reload() {
        loading = true
        financeRows = FinanceDashboardApi.load()
        incomeRows = KundenverbrauchApi.searchAbrechnung(billingMonth, emptyMap())
        allExpenseRows = EingangsbelegeApi.loadBelege()
        expenseRows = allExpenseRows.filter { it.belegdatum.startsWith("$isoMonth-") }
        loading = false
    }
    LaunchedEffect(Unit) {
        reload()
        invoiceCustomers = runCatching { KundenverbrauchApi.loadCompanies() }.getOrDefault(emptyList())
        invoiceCompany = runCatching { UnternehmenstammApi.loadUnternehmen() ?: All() }.getOrDefault(All())
        invoiceUser = LoginService.getUser() ?: All()
        invoiceLogo = LogoSettings.invoiceOverviewLogo
    }
    LaunchedEffect(invoiceToOpen?.id, invoiceToOpen?.datum) {
        val invoice = invoiceToOpen ?: return@LaunchedEffect
        val root = document.getElementById("finanzdashboard-rechnung-export-root") as? HTMLElement
            ?: return@LaunchedEffect
        val entryId = invoice.id.ifBlank { "${invoice.firmenId}_${invoice.datum}" }
        val fileName = "${entryId}_rechnung.pdf"
        val pdfFile = renderLetterDocumentPdfFile(root, fileName)
        val uploadedUrl = ServiceApi.uploadServiceDocument("rechnung", entryId, pdfFile, fileName)
        if (uploadedUrl.isNotBlank()) ServiceApi.openServiceDocument("rechnung", entryId, fileName)
        else AppDialogs.alert("Rechnung konnte nicht geöffnet werden.")
        invoiceToOpen = null
    }
    LaunchedEffect(selectedPeriod, financeRows) {
        if (selectedPeriod != "JAHR" && selectedPeriod != "GESAMT") return@LaunchedEffect
        val monthKeys = financeRows.filter { it.art == "MONAT" }
            .map { it.id }
            .filter { selectedPeriod == "GESAMT" || it.startsWith("$year-") }
            .map { it.replace('-', '_') }
        periodIncomeRows = buildList {
            monthKeys.forEach { monthKey ->
                addAll(KundenverbrauchApi.searchAbrechnung(monthKey, emptyMap()).filter { row ->
                    row.gesamtbetragEuro.replace(',', '.').toDoubleOrNull()?.let { it != 0.0 } == true
                }.map { row -> row.copy(id = "${row.id.ifBlank { row.firmenId }}_$monthKey", datum = monthKey) })
            }
        }
    }

    val current = financeRows.firstOrNull { it.art == "AKTUELLER_MONAT" } ?: All()
    val currentYear = financeRows.firstOrNull { it.art == "AKTUELLES_JAHR" } ?: All()
    val total = financeRows.firstOrNull { it.art == "GESAMT" } ?: All()
    val months = financeRows.filter { it.art == "MONAT" }
    val deductions = financeRows.filter { it.art == "ABGABE" }
    val selectedSummary = when (selectedPeriod) {
        "JAHR" -> currentYear
        "GESAMT" -> total
        else -> current
    }
    val tiles = listOf(
        All(key = "EINNAHMEN", label = "Einnahmen", bezeichnung = financeEuro(selectedSummary.einnahmenBrutto), icon = "images/euro.svg", art = "GRUEN"),
        All(key = "AUSGABEN", label = "Ausgaben", bezeichnung = financeEuro(selectedSummary.ausgabenBrutto), icon = "images/euro.svg", art = "ROT"),
        All(key = "MWST", label = "MwSt.-Guthaben", bezeichnung = financeEuro(selectedSummary.ausgabenMwst), icon = "images/euro.svg", art = "GRUEN"),
        All(key = "GEWINN", label = "Gewinn", bezeichnung = financeEuro(selectedSummary.gewinn), icon = "images/wallet.svg"),
        All(key = "AUSZAHLUNG", label = "Verfügbar zur Entnahme", bezeichnung = financeEuro(selectedSummary.auszahlung), icon = "images/euro.svg")
    )

    Div({ classes(CUSCeoDashboardStyles.screenRoot) }) {
        Div({ classes(CUSCeoDashboardStyles.stickyTop) }) {
            Div({
                style {
                    property("display", "flex")
                    property("justify-content", "center")
                    property("gap", "30px")
                    property("padding", "8px 16px 16px")
                }
            }) {
                listOf("MONAT" to "Monat", "JAHR" to "Jahr", "GESAMT" to "Gesamt").forEach { (key, label) ->
                    Div({
                        style {
                            property("padding", "8px 4px 6px")
                            property("cursor", "pointer")
                            property("font-weight", if (selectedPeriod == key) "700" else "500")
                            property("border-bottom", if (selectedPeriod == key) "3px solid ${UISettings.ACCENT}" else "3px solid transparent")
                        }
                        onClick { selectedPeriod = key }
                    }) { Text(label) }
                }
            }
            Div({ classes(CUSCeoDashboardStyles.tileScrollHost) }) {
                Div({
                    classes(CUSCeoDashboardStyles.grid)
                    style {
                        gridTemplateColumns("repeat(5, 240px)")
                        property("width", "max-content")
                        property("margin-left", "auto")
                        property("margin-right", "auto")
                    }
                }) {
                    tiles.forEach { tile ->
                        Div({
                            if (selectedArea == tile.key) classes(CUSCeoDashboardStyles.tile, CUSCeoDashboardStyles.tileClickable, CUSCeoDashboardStyles.tileActive)
                            else classes(CUSCeoDashboardStyles.tile, CUSCeoDashboardStyles.tileClickable)
                            onClick { selectedArea = tile.key }
                        }) {
                            Div({ classes(CUSCeoDashboardStyles.iconSlot) }) {
                                Img(src = tile.icon, attrs = {
                                    classes(CUSCeoDashboardStyles.icon)
                                    if (tile.art == "GRUEN") style { property("filter", "brightness(0) saturate(100%) invert(63%) sepia(71%) saturate(531%) hue-rotate(88deg) brightness(92%) contrast(91%)") }
                                    if (tile.art == "ROT") style { property("filter", UISettings.ICON_FILTER_ACCENT) }
                                })
                            }
                            Div({ classes(CUSCeoDashboardStyles.textColumn) }) {
                                Div({ classes(CUSCeoDashboardStyles.tileLabel) }) { Text(tile.label) }
                                Div({ classes(CUSCeoDashboardStyles.value) }) { Text(if (loading) "..." else tile.bezeichnung) }
                            }
                        }
                    }
                }
            }
        }
        Div({ classes(CUSCeoDashboardStyles.tabularArea) }) {
            when (selectedArea to selectedPeriod) {
                "EINNAHMEN" to "MONAT" -> SysDashboardEinnahmenTable(
                    incomeRows,
                    { filters -> scope.launch { incomeRows = KundenverbrauchApi.searchAbrechnung(billingMonth, filters) } },
                    { invoiceToOpen = it }
                )
                "EINNAHMEN" to "JAHR", "EINNAHMEN" to "GESAMT" -> SysDashboardEinnahmenTable(
                    periodIncomeRows,
                    { },
                    { invoiceToOpen = it }
                )
                "AUSGABEN" to "MONAT" -> FinanceExpenseTable(expenseRows, "Ausgaben aktueller Monat")
                "AUSGABEN" to "JAHR" -> FinanceExpenseTable(allExpenseRows.filter { it.belegdatum.startsWith("$year-") }, "Ausgaben $year")
                "AUSGABEN" to "GESAMT" -> FinanceExpenseTable(allExpenseRows, "Ausgaben Gesamt")
                "MWST" to "MONAT" -> FinanceVatExpenseTable(expenseRows, "MwSt.-Guthaben ${financeMonthLabel(isoMonth)}")
                "MWST" to "JAHR" -> FinanceVatExpenseTable(allExpenseRows.filter { it.belegdatum.startsWith("$year-") }, "MwSt.-Guthaben $year")
                "MWST" to "GESAMT" -> FinanceVatExpenseTable(allExpenseRows, "MwSt.-Guthaben Gesamt")
                "GEWINN" to "MONAT" -> FinanceCompanyTable(months.filter { it.id == isoMonth }, "Gewinn ${financeMonthLabel(isoMonth)}")
                "GEWINN" to "JAHR" -> FinanceCompanyTable(months.filter { it.id.startsWith("$year-") }, "Gewinn $year")
                "GEWINN" to "GESAMT" -> FinanceCompanyTable(months, "Gewinn Gesamt")
                "AUSZAHLUNG" to "MONAT" -> FinancePayoutTable(months.filter { it.id == isoMonth }, deductions.filter { it.monat == isoMonth }, "Verfügbar zur Entnahme ${financeMonthLabel(isoMonth)}")
                "AUSZAHLUNG" to "JAHR" -> FinancePayoutTable(months.filter { it.id.startsWith("$year-") }, deductions.filter { it.monat.startsWith("$year-") }, "Verfügbar zur Entnahme $year")
                "AUSZAHLUNG" to "GESAMT" -> FinancePayoutTable(months, deductions, "Verfügbar zur Entnahme Gesamt")
            }
        }
        invoiceToOpen?.let { invoice ->
            val customer = invoiceCustomers.firstOrNull {
                it.firmenId.ifBlank { it.id } == invoice.firmenId
            } ?: invoice
            Div({
                id("finanzdashboard-rechnung-export-root")
                style {
                    position(Position.Fixed)
                    left((-10000).px)
                    top(0.px)
                    width(794.px)
                    property("z-index", "-1")
                    property("pointer-events", "none")
                }
            }) {
                RenderInvoiceLetterSys(
                    rechnung = invoice,
                    kunde = customer,
                    unternehmen = invoiceCompany,
                    logoSrc = invoiceLogo,
                    currentSysUser = invoiceUser
                )
            }
        }
    }
}

@Composable
private fun FinanceExpenseTable(rows: List<All>, title: String) {
    val headers = listOf("Belegdatum", "Belegnummer", "Händler", "Positionen", "Netto", "MwSt.", "Brutto")
    FinanceSmartTable(
        key = "FinanzDashboard_$title", title = title, headers = headers,
        data = rows.map { row -> row.id to listOf(row.belegdatum, row.belegnummer, row.haendler, row.positionAnzahl.toString(), row.netto, row.mwst, row.brutto) }
    )
}

@Composable
private fun FinanceCompanyTable(rows: List<All>, title: String) = FinanceSmartTable(
    key = "FinanzDashboard_${title}", title = title,
    headers = listOf("Monat", "Einnahmen", "MwSt. ans Finanzamt", "Ausgaben", "MwSt. zurück", "Gewinn"),
    data = rows.map { row -> row.id to listOf(
        financeMonthLabel(row.id), row.einnahmenBrutto, row.einnahmenMwst,
        row.ausgabenBrutto, row.ausgabenMwst, row.gewinn
    ) }
)

@Composable
private fun FinanceVatExpenseTable(rows: List<All>, title: String) {
    val receiptRows = rows.map { row -> row.id to listOf(
        row.belegdatum, row.belegnummer, row.haendler, row.brutto, row.netto, row.mwst
    ) }
    val totalRow = "SUMME" to listOf(
        "", "", "Gesamtsumme",
        financeEuro(rows.sumOf { financeAmount(it.brutto) }.toString()),
        financeEuro(rows.sumOf { financeAmount(it.netto) }.toString()),
        financeEuro(rows.sumOf { financeAmount(it.mwst) }.toString())
    )
    FinanceSmartTable(
        key = "FinanzDashboard_${title}", title = title,
        headers = listOf("Datum", "Belegnummer", "Händler", "Bezahlt", "Ohne Steuer", "MwSt. zurück"),
        data = receiptRows + totalRow
    )
}

@Composable
private fun FinancePayoutTable(rows: List<All>, deductions: List<All>, title: String) = FinanceSmartTable(
    key = "FinanzDashboard_${title}", title = title,
    headers = listOf(
        "Monat", "Gewinn", "Einkommensteuer", "Solidaritätszuschlag", "Kirchensteuer", "Gewerbesteuer",
        "Krankenkasse", "Pflegeversicherung", "Rentenversicherung", "Sonstige Abgaben", "Verfügbar zur Entnahme"
    ),
    data = rows.map { row ->
        val monthDeductions = deductions.filter { it.monat == row.id }
        fun amount(name: String): String = monthDeductions.firstOrNull { it.bezeichnung == name }?.betrag.orEmpty().ifBlank { "0.00" }
        row.id to listOf(
            financeMonthLabel(row.id), row.gewinn,
            amount("Einkommensteuer"), amount("Solidaritätszuschlag"), amount("Kirchensteuer"),
            amount("Gewerbesteuer"), amount("Krankenkasse"),
            amount("Pflegeversicherung"), amount("Rentenversicherung"),
            amount("Sonstige betriebliche Abgaben"), row.auszahlung
        )
    }
)

@Composable
private fun FinanceSmartTable(
    key: String,
    title: String,
    headers: List<String>,
    data: List<Pair<String, List<String>>>,
    onSearch: ((Map<String, String>) -> Unit)? = null,
    onCellClick: ((String, Int) -> Unit)? = null
) {
    Div({ classes(CUSCeoDashboardStyles.verbrauchTable) }) {
        TabularScreen(
            screenKey = key, rollenKey = "FinanzDashboard", title = title, headers = headers, data = data,
            editable = false, hardReadOnly = true, includeMetadata = false, nonEditableCols = headers.toSet(),
            allowCreate = false, allowDelete = false, allowSave = false, allowImportButton = false,
            allowExportButton = true, allowRefreshButton = true, allowSearchButton = true, allowSettingsButton = true,
            useUserSettings = false, smartMode = true, onSearchWithFilters = onSearch, onCellClick = onCellClick, onCommit = {}
        )
    }
}
